import React, { useState, useContext, useMemo } from "react";
import { signup } from "../api";
import { Link, useNavigate } from "react-router-dom";
import { ThemeContext } from "../App";
import logo from "../assets/astratrade-logo.svg";

const OTP_LENGTH = 6;

export default function Signup() {
  const [username, setUsername] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [msg, setMsg] = useState("");
  const [error, setError] = useState("");
  const [isSuccess, setIsSuccess] = useState(false);
  const [otp, setOtp] = useState("");
  const [otpSent, setOtpSent] = useState(false);
  const [infoMsg, setInfoMsg] = useState("");
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const { theme, toggleTheme } = useContext(ThemeContext);

  const classes = useMemo(() => {
    const baseBackground =
      theme === "dark"
        ? "bg-slate-950 text-white"
        : "bg-slate-50 text-gray-900";
    const card =
      theme === "dark"
        ? "bg-gray-800 text-white"
        : "bg-white text-gray-900 border border-gray-200";
    const input =
      theme === "dark"
        ? "w-full p-2 mb-4 rounded bg-gray-900 border border-gray-600 text-white placeholder-gray-400"
        : "w-full p-2 mb-4 rounded bg-white border border-gray-300 text-gray-900 placeholder-gray-500";
    const successBox =
      theme === "dark"
        ? "bg-green-600 text-white"
        : "bg-green-100 text-green-800";
    const errorBox =
      theme === "dark" ? "bg-red-600 text-white" : "bg-red-100 text-red-800";
    const infoBox =
      theme === "dark" ? "bg-blue-600 text-white" : "bg-blue-100 text-blue-800";
    return { baseBackground, card, input, successBox, errorBox, infoBox };
  }, [theme]);

  const handleSignup = async () => {
    if (loading) return;

    const trimmedUsername = username.trim();
    const trimmedEmail = email.trim();

    if (!otpSent) {
      if (!trimmedUsername || !trimmedEmail || !password || !confirmPassword) {
        setError("Please fill in all fields");
        return;
      }

      if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmedEmail)) {
        setError("Enter a valid email address");
        return;
      }

      if (password !== confirmPassword) {
        setError("Passwords do not match");
        return;
      }

      if (password.length < 6) {
        setError("Password must be at least 6 characters long");
        return;
      }

      try {
        setLoading(true);
        setError("");
        setInfoMsg("");
        setIsSuccess(false);
        setMsg("");
        const res = await signup(
          trimmedUsername,
          trimmedEmail.toLowerCase(),
          password
        );
        setOtpSent(true);
        setOtp("");
        setInfoMsg(res?.message || "Verification code sent to your email.");
      } catch (err) {
        console.error("Signup error:", err);
        setError(err.response?.data?.detail || "Signup failed.");
      } finally {
        setLoading(false);
      }
      return;
    }

    if (!otp.trim()) {
      setError("Enter the verification code sent to your email");
      return;
    }

    try {
      setLoading(true);
      setError("");
      const res = await signup(
        trimmedUsername,
        trimmedEmail.toLowerCase(),
        password,
        otp.trim()
      );
      setMsg(res?.message || "Signup successful! You can now login.");
      setInfoMsg("");
      setIsSuccess(true);

      setTimeout(() => {
        navigate("/");
      }, 2000);
    } catch (err) {
      console.error("Signup verification error:", err);
      setError(err.response?.data?.detail || "Verification failed.");
    } finally {
      setLoading(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === "Enter") {
      handleSignup();
    }
  };

  return (
    <div
      className={`flex justify-center items-center min-h-screen transition-colors duration-200 ${classes.baseBackground}`}
    >
      <div
        className={`p-8 rounded-lg shadow-lg w-96 transition-colors duration-200 ${classes.card}`}
      >
        <div className="flex justify-center mb-6">
          <img src={logo} alt="AstraTrade logo" className="h-16 w-auto" />
        </div>
        <div className="flex items-center justify-between mb-4">
          <h1 className="text-2xl font-bold">Sign Up</h1>
          <button
            className="text-sm px-3 py-1 rounded border border-current"
            onClick={toggleTheme}
          >
            {theme === "dark" ? "Light Mode" : "Dark Mode"}
          </button>
        </div>

        {error && (
          <div className={`${classes.errorBox} p-2 rounded mb-4 text-center`}>
            {error}
          </div>
        )}

        {infoMsg && !isSuccess && (
          <div className={`${classes.infoBox} p-2 rounded mb-4 text-center`}>
            {infoMsg}
          </div>
        )}

        {isSuccess && (
          <div className={`${classes.successBox} p-2 rounded mb-4 text-center`}>
            {msg}
            <br />
            <small>Redirecting to login...</small>
          </div>
        )}

        <input
          type="text"
          placeholder="Username"
          value={username}
          onChange={(e) => setUsername(e.target.value)}
          onKeyPress={handleKeyPress}
          className={classes.input}
          disabled={otpSent || loading}
        />
        <input
          type="email"
          placeholder="Email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          onKeyPress={handleKeyPress}
          className={classes.input}
          disabled={otpSent || loading}
        />
        <input
          type="password"
          placeholder="Password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          onKeyPress={handleKeyPress}
          className={classes.input}
          disabled={otpSent || loading}
        />
        <input
          type="password"
          placeholder="Confirm Password"
          value={confirmPassword}
          onChange={(e) => setConfirmPassword(e.target.value)}
          onKeyPress={handleKeyPress}
          className={classes.input}
          disabled={otpSent || loading}
        />
        {otpSent && !isSuccess && (
          <input
            type="text"
            inputMode="numeric"
            placeholder="Enter OTP"
            value={otp}
            onChange={(e) =>
              setOtp(e.target.value.replace(/\D+/g, "").slice(0, OTP_LENGTH))
            }
            onKeyPress={handleKeyPress}
            className={classes.input}
            autoFocus
            disabled={loading}
          />
        )}
        <button
          onClick={handleSignup}
          className="w-full bg-green-500 text-white p-2 rounded font-bold hover:bg-green-600 mb-4 disabled:opacity-60"
          disabled={loading || isSuccess}
        >
          {isSuccess
            ? "Success!"
            : otpSent
            ? loading
              ? "Verifying..."
              : "Verify OTP"
            : loading
            ? "Sending..."
            : "Send Verification Code"}
        </button>

        <div className="text-center">
          <Link to="/" className="text-blue-500 hover:underline">
            Already have an account? Login here
          </Link>
        </div>
      </div>
    </div>
  );
}
